import { useTranslation } from 'react-i18next';
import { HpTag, statusLabel, statusTone, type HpTagTone } from '@hp/ui';

/** Machine status → design-system pill; colour comes from the shared status → tone map, never a call site. */
export function StatusBadge({ status }: { status: string | null | undefined }) {
  const { t } = useTranslation();
  if (!status) return <span className="bo-muted">—</span>;
  return <HpTag tone={statusTone(status)}>{t(`status.${status}`, { defaultValue: statusLabel(status) })}</HpTag>;
}

export function TierBadge({ tier }: { tier: string | null | undefined }) {
  const { t } = useTranslation();
  if (!tier) return <span className="bo-muted">—</span>;
  return <HpTag tone={tier === 'auto' ? 'success' : 'info'}>{t(`tier.${tier}`, { defaultValue: tier })}</HpTag>;
}

export function EntityBadge({ entityType }: { entityType: string | null | undefined }) {
  const { t } = useTranslation();
  if (!entityType) return <span className="bo-muted">—</span>;
  const key = entityType === 'organization' ? 'org' : entityType;
  return <HpTag tone="neutral">{t(`entity.${key}`, { defaultValue: key })}</HpTag>;
}

const MODE_TONE: Record<string, HpTagTone> = { dry_run: 'neutral', pilot: 'warning', full: 'danger', live: 'danger' };

export function ModeBadge({ mode }: { mode: string | null | undefined }) {
  const { t } = useTranslation();
  if (!mode) return <span className="bo-muted">—</span>;
  return <HpTag tone={MODE_TONE[mode] ?? 'neutral'}>{t(`mode.${mode}`, { defaultValue: statusLabel(mode) })}</HpTag>;
}

/** Every rule code the dedup engine emits (`modules/dedup/domain/rules.ts`); the catalogs name each one. */
const RULE_CODES = [
  'P1',
  'P2',
  'P3',
  'P4',
  'P5',
  'P6',
  'P7',
  'O1',
  'O2',
  'O3',
  'O4',
  'O5',
  'O6',
  'SHARED_ENDPOINT_DEMOTION',
] as const;
type RuleCode = (typeof RULE_CODES)[number];
const isRuleCode = (rule: string): rule is RuleCode => (RULE_CODES as readonly string[]).includes(rule);

/**
 * Dedup rule codes as quiet chips wearing their human-readable name; the code and the full
 * explanation sit in the tooltip. A code the catalogs do not know falls back to the raw code.
 */
export function RuleChips({ rules }: { rules: string[] | null | undefined }) {
  const { t } = useTranslation();
  if (!rules || rules.length === 0) return <span className="bo-muted">—</span>;
  return (
    <span className="bo-chips">
      {rules.map((rule) => {
        const known = isRuleCode(rule);
        return (
          <span key={rule} className="bo-chip" title={known ? `${rule} — ${t(`rules.${rule}.hint`)}` : rule}>
            {known ? t(`rules.${rule}.label`) : rule}
          </span>
        );
      })}
    </span>
  );
}
